"use client";
import MetricCard from "@/components/dashboard/MetricCard";
import DateRangePicker from "@/components/form/date-range-picker";
import { fetchMetrics } from "@/utils/api";
import { useEffect, useState } from "react";

// Define types for better type safety
interface Metric {
  title: string;
  value: string | number;
  change: string;
  changeType: "positive" | "negative" | "neutral";
  chartData: number[];
  info?: boolean;
}

interface MetricsData {
  main: Metric[];
}

// Calculate default date ranges
const today = new Date();
const startMain = new Date(today);
startMain.setDate(today.getDate() - 29); // 30 days including today
const endMain = new Date(today);

const startCompare = new Date(today);
startCompare.setDate(today.getDate() - 59);
const endCompare = new Date(today);
endCompare.setDate(today.getDate() - 30);

export default function DashboardOverviewClient() {
  const [mainRange, setMainRange] = useState<{ dates: Date[]; dateStr: string }>({
    dates: [startMain, endMain],
    dateStr: "",
  });
  const [compareRange, setCompareRange] = useState<{ dates: Date[]; dateStr: string }>({
    dates: [startCompare, endCompare],
    dateStr: "",
  });
  const [showCompare, setShowCompare] = useState(true);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mainRange.dates[0] || !mainRange.dates[1]) return;
    setLoading(true);
    const start = mainRange.dates[0].toISOString().split("T")[0];
    const end = mainRange.dates[1].toISOString().split("T")[0];

    // Include comparison dates when showCompare is true - use selected dates or fallback to previous period
    let compareStart: string | undefined;
    let compareEnd: string | undefined;
    if (showCompare) {
      if (compareRange.dates[0] && compareRange.dates[1]) {
        compareStart = compareRange.dates[0].toISOString().split("T")[0];
        compareEnd = compareRange.dates[1].toISOString().split("T")[0];
      } else {
        // Fallback: previous period of same length as main range
        const mainStart = mainRange.dates[0].getTime();
        const mainEnd = mainRange.dates[1].getTime();
        const mainDuration = mainEnd - mainStart;
        const compareEndDate = new Date(mainStart - 1); // Day before main start
        const compareStartDate = new Date(compareEndDate.getTime() - mainDuration);
        compareStart = compareStartDate.toISOString().split("T")[0];
        compareEnd = compareEndDate.toISOString().split("T")[0];
      }
    }

    fetchMetrics(start, end, compareStart, compareEnd)
      .then((response) => {
        const metricsData = response.data as MetricsData;
        setMetrics(metricsData?.main || []);
      })
      .catch((error: Error) => {
        console.error('Error fetching metrics:', error);
        setMetrics([]);
      })
      .finally(() => setLoading(false));
  }, [mainRange, compareRange, showCompare]);

  return (
    <div className="col-span-12 mb-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Reports overview</h2>
        <div className="flex flex-wrap items-center gap-4">
          <DateRangePicker
            id="main-date-range-picker"
            label={undefined}
            placeholder="Select date range"
            defaultDate={{ startDate: mainRange.dates[0], endDate: mainRange.dates[1] }}
            onChange={(dates, dateStr) => {
              setMainRange({ dates, dateStr });
            }}
          />
          {showCompare && (
            <DateRangePicker
              id="compare-date-range-picker"
              label={undefined}
              placeholder="Select date range"
              defaultDate={{ startDate: compareRange.dates[0], endDate: compareRange.dates[1] }}
              onChange={(dates, dateStr) => {
                setCompareRange({ dates, dateStr });
              }}
            />
          )}
          <button
            className="h-11 px-3 border rounded-lg border-gray-300 text-gray-800 bg-white dark:border-gray-600 dark:text-white dark:bg-gray-800"
            onClick={() => setShowCompare(v => !v)}
          >
            {showCompare ? "Hide Comparison" : "Compare Period"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {loading ? (
          <div className="col-span-4 text-center py-8 text-gray-500 dark:text-gray-400">Loading metrics...</div>
        ) : metrics.length > 0 ? (
          metrics.map((metric, idx) => (
            <MetricCard key={idx} {...metric} showDetails={showCompare} />
          ))
        ) : (
          <div className="col-span-4 text-center py-8 text-gray-500 dark:text-gray-400">No metrics found for selected period.</div>
        )}
      </div>
    </div>
  );
} 